import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useEffect, useMemo, useState } from "react";

import {
  filterDrawLibraryCatalog,
  type DrawLibraryCatalogItem,
  type DrawLibraryCategoryOption,
  type DrawLibraryCatalogStatus,
  type DrawLibraryInsertMode,
} from "../../src/services/drawLibraryService";

export const DrawLibraryDialog = ({
  catalog,
  categories,
  selectedCategory,
  searchQuery,
  status,
  error,
  isInserting,
  onCategoryChange,
  onSearchChange,
  onInsert,
  onRetry,
  onClose,
}: {
  catalog: DrawLibraryCatalogItem[];
  categories: DrawLibraryCategoryOption[];
  selectedCategory: string;
  searchQuery: string;
  status: DrawLibraryCatalogStatus;
  error: string | null;
  isInserting: boolean;
  onCategoryChange: (category: string) => void;
  onSearchChange: (query: string) => void;
  onInsert: (
    item: DrawLibraryCatalogItem,
    mode: DrawLibraryInsertMode,
  ) => void | Promise<void>;
  onRetry: () => void | Promise<void>;
  onClose: () => void;
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const filteredCatalog = useMemo(
    () =>
      filterDrawLibraryCatalog({
        catalog,
        category: selectedCategory,
        query: searchQuery,
      }),
    [catalog, searchQuery, selectedCategory],
  );
  const selectedItem = useMemo(
    () => filteredCatalog.find((item) => item.id === selectedItemId) || null,
    [filteredCatalog, selectedItemId],
  );
  const isLoading = status === "idle" || status === "loading";

  useEffect(() => {
    if (!filteredCatalog.length) {
      setSelectedItemId(null);
      return;
    }

    if (!selectedItemId || !filteredCatalog.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(filteredCatalog[0].id);
    }
  }, [filteredCatalog, selectedItemId]);

  return (
    <Dialog
      title="Biblioteca Draw"
      size={960}
      onCloseRequest={onClose}
      closeOnClickOutside
    >
      <div className="draw-library-dialog">
        <div className="draw-library-dialog__toolbar">
          <label className="draw-library-dialog__field">
            <span>Categoria</span>
            <select
              value={selectedCategory}
              disabled={isLoading || isInserting}
              onChange={(event) => onCategoryChange(event.target.value)}
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </label>

          <label className="draw-library-dialog__field draw-library-dialog__search">
            <span>Busca</span>
            <input
              value={searchQuery}
              disabled={isLoading || isInserting}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Nome, tag ou categoria"
              type="search"
            />
          </label>
        </div>

        {error && (
          <div className="draw-library-dialog__message draw-library-dialog__message--error">
            <span>{error}</span>
            <button disabled={isLoading} type="button" onClick={onRetry}>
              Recarregar
            </button>
          </div>
        )}

        {isLoading && !error && (
          <div className="draw-library-dialog__message">
            Carregando Biblioteca Draw...
          </div>
        )}

        {!isLoading && !error && (
          <div className="draw-library-dialog__content">
            <div className="draw-library-dialog__list" role="listbox">
              {!filteredCatalog.length && (
                <div className="draw-library-dialog__empty">
                  Nenhuma biblioteca encontrada.
                </div>
              )}

              {filteredCatalog.map((item) => (
                <button
                  key={item.id}
                  className="draw-library-dialog__item"
                  data-selected={item.id === selectedItemId}
                  disabled={isInserting}
                  onClick={() => setSelectedItemId(item.id)}
                  type="button"
                >
                  <strong>{item.name}</strong>
                  <span>{item.category}</span>
                  <small>{item.tags.slice(0, 6).join(", ")}</small>
                </button>
              ))}
            </div>

            <div className="draw-library-dialog__details">
              {selectedItem ? (
                <>
                  <div className="draw-library-dialog__details-text">
                    <strong>{selectedItem.name}</strong>
                    <span>{selectedItem.category}</span>
                    <p>{selectedItem.description}</p>
                    <div className="draw-library-dialog__tags">
                      {selectedItem.tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </div>

                  <div className="draw-library-dialog__actions">
                    <button
                      className="draw-library-dialog__primary"
                      disabled={isInserting}
                      onClick={() => onInsert(selectedItem, "insert")}
                      type="button"
                    >
                      Inserir no canvas
                    </button>
                    <button
                      className="draw-library-dialog__secondary"
                      disabled={isInserting}
                      onClick={() => onInsert(selectedItem, "append-below")}
                      type="button"
                    >
                      Inserir abaixo do conteudo atual
                    </button>
                    <button
                      className="draw-library-dialog__danger"
                      disabled={isInserting}
                      onClick={() => onInsert(selectedItem, "replace")}
                      type="button"
                    >
                      Substituir canvas atual
                    </button>
                  </div>
                </>
              ) : (
                <div className="draw-library-dialog__empty">
                  Selecione uma biblioteca para inserir.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
